import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import useSnackbar from "../../../hooks/digitalMarketing/useSnackbar";
import Toast from "../../../Component/DigitalMarketing/Toast";
import BackdropLoader from "../../../Component/DigitalMarketing/BackDropLoader";
import CustomInput from "../../../Component/DigitalMarketing/CustomInput";
import { useGetDoctorListQuery } from "../../../Service/reports";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useCreateConsultantPaymentMutation,
  useGetConsultantAppointmentCalculateQuery,
} from "../../../Service/consultant";

const AddConsultant = (props) => {
  const { setShow, view } = props;
  const { open, message, openSnackBar, type } = useSnackbar();

  // ✅ Get consultants
  const { data: doctors, isLoading: doctorsLoading } = useGetDoctorListQuery(
    null,
    { refetchOnMountOrArgChange: true }
  );

  // ✅ Create Payment API hook
  const [createPayment, { isLoading: isSubmitting }] =
    useCreateConsultantPaymentMutation();

  const [formData, setFormData] = useState({
    consultantId: "",
    startDate: "",
    endDate: "",
    totalConsultants: "",
    totalAmount: 0,
    totalInWords: "",
    refferName: "",
  });

  // ✅ Trigger GET API for calculation when all values selected
  const shouldFetch =
    formData.consultantId && formData.startDate && formData.endDate;

  const { data: consultantData, isFetching } =
    useGetConsultantAppointmentCalculateQuery(
      shouldFetch
        ? {
            startDate: formData.startDate,
            endDate: formData.endDate,
            consultantId: formData.consultantId,
          }
        : skipToken
    );

  // ✅ Autofill values from GET response
  useEffect(() => {
    if (consultantData?.data) {
      setFormData((prev) => ({
        ...prev,
        totalConsultants: consultantData.data.appointmentCount || 0,
        totalAmount: consultantData.data.totalRevenue || 0,
        totalInWords: consultantData.data.totalRevenueInWords || "",
      }));
    }
  }, [consultantData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ✅ Handle Submit (POST API)
  const handleSubmit = async () => {
    if (
      !formData.consultantId ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.totalConsultants ||
      !formData.totalAmount ||
      !formData.refferName
    ) {
      openSnackBar("Please fill all required fields", "error");
      return;
    }

    try {
      const payload = {
        endDate: formData.endDate,
        startDate: formData.startDate,
        consultantId: Number(formData.consultantId),
        totalAppointment: Number(formData.totalConsultants),
        totalAmount: Number(formData.totalAmount),
        refferName: formData.refferName,
      };

      const res = await createPayment(payload).unwrap();
      if (res?.status === "true" || res?.status === true) {
        openSnackBar("Payment shared successfully", "success");
        setTimeout(() => setShow(false), 700);
      } else {
        openSnackBar(res?.message || "Failed to share payment", "error");
      }
    } catch (err) {
      openSnackBar("Error sharing payment", "error");
    }
  };

  return (
    <div className="p-3">
      <BackdropLoader loader={isFetching || doctorsLoading || isSubmitting} />
      <Toast open={open} type={type} message={message} />

      {!view && (
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <h5 className="fw-600 m-0">Add Payment</h5>
          <span
            style={{ cursor: "pointer", fontSize: "20px" }}
            onClick={() => setShow(false)}
          >
            ✕
          </span>
        </div>
      )}

      {/* Consultant Dropdown */}
      <div className="mb-1">Select Consultant</div>
      <CustomInput
        type="select"
        option={
          doctors?.data?.map((doc) => ({
            value: doc.id,
            label: `${doc.consultantName} (${doc.speciality})`,
          })) || []
        }
        name="consultantId"
        value={formData.consultantId}
        onChange={handleChange}
        disabled={doctorsLoading}
        labelKey="label"
        valueKey="value"
      />

      {/* Date Fields */}
      <div className="mb-3 mt-3">
        <label className="fw-500">Select start and end date</label>
        <input
          type="date"
          name="startDate"
          value={formData.startDate}
          onChange={handleChange}
          className="form-control mt-1"
        />
        <input
          type="date"
          name="endDate"
          value={formData.endDate}
          onChange={handleChange}
          className="form-control mt-2"
        />
      </div>

      {/* Total Consultant Count (Read Only) */}
      <div className="mb-3">
        <label className="fw-500">Total consultant attended</label>
        <input
          type="text"
          name="totalConsultants"
          value={formData.totalConsultants}
          readOnly
          className="form-control mt-1 bg-light"
        />
      </div>

      {/* Total Amount (Read Only) */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center">
          <span
            style={{
              fontWeight: "600",
              color: "#6C47FF",
              fontSize: "18px",
            }}
          >
            Total ₹{" "}
            {Number(formData.totalAmount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      {/* Total in Words (Read Only) */}
      <div className="mb-3">
        <label className="fw-500">Total in Words</label>
        <input
          type="text"
          name="totalInWords"
          value={formData.totalInWords}
          readOnly
          className="form-control mt-1 bg-light"
        />
      </div>

      {/* Referral Name (Typing Field) */}
      <div className="mb-3">
        <label className="fw-500">Referral Name</label>
        <input
          type="text"
          name="refferName"
          value={formData.refferName}
          onChange={handleChange}
          placeholder="Enter referral name"
          className="form-control mt-1"
        />
      </div>

      {/* Submit Button */}
      <div className="text-center mt-4">
        <Button
          variant="contained"
          id="btn-save"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#6C47FF",
            textTransform: "none",
            padding: "10px 40px",
            borderRadius: "8px",
          }}
        >
          {isSubmitting ? "Processing..." : "Proceed to payment"}
        </Button>
      </div>
    </div>
  );
};

export default AddConsultant;
